// Sammel-Nachbesserung Schritt 4 (Q1 nach P-1, S-3 nach V-S3 und V-S3b): welche Zusatzrollen die Seite „Rollen &
// Berechtigungen“ (Roles.tsx) je Person anbietet und wann die Auswahl gesperrt ist. Entschieden wird nur über die
// Felder von GET /roles, nie über Rollennamen (M-01.6). Der Server prüft dasselbe (permissions.ts, assign); die
// Oberfläche bietet nur nicht an, was er sicher abweist.
export type ChoiceRole = { id: string; permissions?: string[]; adminFloor?: boolean; ownerFloor?: boolean };
export type ChoiceMember = { organizationAdmin?: boolean; restricted?: boolean; owner?: boolean; self?: boolean; scopedRoleId?: string | null };

// Die Mandantenrechte eines Mitglieds: die Rechte seiner Zusatzrolle und dazu „Organisation ansehen“
// (membershipCapabilities in backend/src/administration/capabilities.ts).
function memberRights(member: ChoiceMember, roles: ChoiceRole[]): string[] {
  const own = roles.find(role => role.id === member.scopedRoleId)?.permissions ?? [];
  return own.length ? ['organization.read', ...own] : [];
}

export function roleChoices<R extends ChoiceRole>(member: ChoiceMember, roles: R[]): R[] {
  // Die Rolle, die die Auswahl gerade zeigt, bleibt angeboten; sonst stünde dort fälschlich der erste Eintrag.
  const current = member.organizationAdmin && !member.restricted ? null : member.scopedRoleId ?? null;
  const own = member.self && !member.organizationAdmin ? memberRights(member, roles) : null;
  return roles.filter(role => role.id === current || (member.organizationAdmin
    // E-2: ein Administrator nur mit der Untergrenze; P-1: der Besitzer dazu mit Abrechnung und Kosten.
    ? !!role.adminFloor && (!member.owner || !!role.ownerFloor)
    // V-S3b: in der eigenen Zeile nur Rollen, deren Rechte das Mitglied schon trägt; fremde Zeilen jede Rolle.
    : !own || (role.permissions ?? []).every(permission => own.includes(permission))));
}

// S-3 (V-S3): die Zeile eines Administrators ist gesperrt, wenn es die eigene ist oder dem Handelnden Rollen- oder
// Personenpflege fehlt (mayRestrictAdmins). Die Zeilen von Mitgliedern bleiben wählbar.
export function roleSelectDisabled(member: ChoiceMember, { canWrite, busy, mayRestrictAdmins }: { canWrite: boolean; busy: boolean; mayRestrictAdmins: boolean }): boolean {
  return !canWrite || busy || (!!member.organizationAdmin && (!!member.self || !mayRestrictAdmins));
}

// Admin-Wünsche W6 (output/admin-wuensche-20261008/PLAN.md 2.6; Schritt M-3): die Spalte „Organisationsrolle“ (Mitglied /
// Administrator) auf der Rollenseite. Gesperrt mit Grund in der Zelle, nur über Felder von GET /roles: die eigene Zeile
// (F08-B1, V-S3b), der Besitzer als Administrator (P-1, OWNER_PROTECTED), ein Plattformkonto und ohne Rollen- und
// Personenpflege (mayRestrictAdmins = roles.write und members.write, wie updateMember verlangt). Den letzten Verwalter
// prüft nur der Server (LAST_ADMIN); seine Meldung steht im Dialog.
// Die Werte der Auswahl sind Wörter der Oberfläche; erst der Dialog übersetzt sie in die Mitgliedschaftsart, die er setzt
// (Beschriftung und Vergabe, keine Zugriffsentscheidung; Wächter waechter-rollennamen.test.cjs, M-01.6).
export type OrganisationsrolleWahl = 'mitglied' | 'administrator';
export const MITGLIEDSCHAFTSART = { mitglied: 'MEMBER', administrator: 'ADMIN' } as const;
export const ORGANISATIONSROLLE_GRUND = {
  selbst: 'Ihr Konto',
  // Plan 1a Welle 1, O2-4: derselbe Satz wie mitgliedTexte.ts GRUND.besitzerRolle; die Zelle zeigt dazu „Besitzer“.
  besitzer: 'Bleibt bis zur Übergabe Besitzer.',
  // F-23 (PF-NB-firma-3, Fehlersuche Nr. 16): der Grund statt eines eigenen Rollenworts; dieselbe Zeile in mitgliedTexte.ts GRUND.
  plattform: 'Plattformkonto: ändern kann das nur, wer Betreiberrechte vergibt.',
  recht: 'Nur mit „Rollen und Zuordnungen pflegen“ und „Mitgliedschaften und Plätze pflegen“.',
} as const;
export function organisationsrolleGesperrt(member: ChoiceMember & { platformAccount?: boolean }, { canWrite, mayRestrictAdmins }: { canWrite: boolean; mayRestrictAdmins: boolean }): string | null {
  if (member.platformAccount) return ORGANISATIONSROLLE_GRUND.plattform;
  if (member.self) return ORGANISATIONSROLLE_GRUND.selbst;
  if (member.owner && member.organizationAdmin) return ORGANISATIONSROLLE_GRUND.besitzer;
  if (!canWrite || !mayRestrictAdmins) return ORGANISATIONSROLLE_GRUND.recht;
  return null;
}
